import { useState } from 'react';
import { QrCode, Monitor, Users, CheckCircle } from 'lucide-react';

const FeatureShowcase = () => {
  const [activeTab, setActiveTab] = useState('qr-ordering');

  const structuralContent = {
    'qr-ordering': {
      title: "Premium Wood-Crafted QR Showpieces",
      tagline: "The Dining Room Experience",
      description: "Replace cheap plastic stickers with luxury. We provide custom wood-crafted, UV-printed table showpieces tailored to your restaurant's aesthetic. Guests scan to browse your menu, order, and pay instantly without flagging down a busy server.",
      benefits: ["UV-printed on premium natural wood", "Instant guest ordering & split-bill checkout", "Reduces table wait times by up to 12 minutes"],
      // A premium demo image placeholder from the internet showing a restaurant table setup
      displayImage: "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&q=80&w=1000"
    },
    'pos-engine': {
      title: "The Main Counter Cashier Desk",
      tagline: "Front-of-House Control",
      description: "A fast, straightforward screen that groups your walk-in customers, phone take-aways, and automatic table QR orders into one master register. Keeps your daily accounting exact without evening math errors.",
      benefits: ["Tracks cash, card, and digital split-payments", "Syncs instantly with kitchen screens", "One-click evening sales summaries"],
      displayImage: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=1000"
    },
    'attendance': {
      title: "Live Staff Rosters & Attendance",
      tagline: "Back-of-House Coordination",
      description: "Manage your floor teams cleanly without chaotic WhatsApp groups or paper schedules. Floor staff clock in directly at the terminal station, giving you a live view of who is working right from your phone.",
      benefits: ["Staff clock-in via simple secure pins", "Tracks late arrivals and overtime hourly", "Direct shift reporting for clean payroll"],
      displayImage: "https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?auto=format&fit=crop&q=80&w=1000"
    }
  };

  return (
    <section className="w-full min-h-screen bg-zinc-950 text-white relative flex items-center py-24 border-t border-zinc-900" id="features">
      
      {/* Background Lighting Matrix */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] bg-blue-600/5 rounded-full blur-[160px] pointer-events-none"></div>

      <div className="w-full px-6 sm:px-12 lg:px-16 mx-auto relative z-10">
        
        {/* --- MAIN SPLIT CONTAINER --- */}
        <div className="flex flex-col lg:flex-row items-stretch gap-12 lg:gap-16 min-h-[650px]">
          
          {/* 1. VISUAL MULTIMEDIA SLATE (Left Side) */}
          <div className="flex-1 relative rounded-3xl overflow-hidden border border-zinc-800 bg-zinc-900 group">
            
            {/* Full-bleed high-contrast image asset */}
            <img 
              src={structuralContent[activeTab].displayImage} 
              alt={structuralContent[activeTab].title}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out scale-100 group-hover:scale-105"
            />
            
            {/* Dark gradient mapping to protect text overlay readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent"></div>
            
            {/* Bottom Floating Visual Indicator */}
            <div className="absolute bottom-8 left-8 right-8 flex justify-between items-end z-10">
              <div className="space-y-1">
                <p className="text-xs font-mono tracking-widest text-blue-400 uppercase font-bold">
                  {structuralContent[activeTab].tagline}
                </p>
                <h3 className="text-2xl font-black uppercase text-white tracking-tight">
                  {structuralContent[activeTab].title}
                </h3>
              </div>
            </div>

            {/* Top decorative system border lines */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-blue-500 via-cyan-400 to-transparent"></div>
          </div>

          {/* 2. DYNAMIC INTERACTIVE MATRIX (Right Side) */}
          <div className="flex-1 flex flex-col justify-between py-4">
            
            {/* Top Meta Details */}
            <div className="space-y-6">
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 font-mono block">
                  // Core Brand Ecosystem
                </span>
                <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white leading-none">
                  The SOKO Setup
                </h2>
              </div>
              <p className="text-zinc-400 text-base font-medium leading-relaxed max-w-xl">
                We supply the beautiful, natural wood showpieces for your customer tables along with the central software engine that runs your counters and kitchens.
              </p>
            </div>

            {/* Interactive Control Toggles (Replaces static content boxes) */}
            <div className="my-10 space-y-4">
              
              {/* QR Toggle */}
              <button 
                onClick={() => setActiveTab('qr-ordering')}
                className={`w-full text-left p-5 rounded-2xl transition-all duration-300 relative overflow-hidden flex items-center justify-between border ${
                  activeTab === 'qr-ordering' 
                  ? 'bg-zinc-900 border-blue-500 text-white shadow-xl shadow-blue-950/20' 
                  : 'bg-transparent border-zinc-800/80 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <QrCode className={`w-5 h-5 ${activeTab === 'qr-ordering' ? 'text-blue-400' : 'text-zinc-500'}`} />
                  <span className="text-base font-bold uppercase tracking-wide">1. Table QR Showpieces</span>
                </div>
                <span className="text-xs font-mono text-zinc-500 font-bold">[ VIEW SYSTEM ]</span>
              </button>

              {/* POS Toggle */}
              <button 
                onClick={() => setActiveTab('pos-engine')}
                className={`w-full text-left p-5 rounded-2xl transition-all duration-300 relative overflow-hidden flex items-center justify-between border ${
                  activeTab === 'pos-engine' 
                  ? 'bg-zinc-900 border-blue-500 text-white shadow-xl shadow-blue-950/20' 
                  : 'bg-transparent border-zinc-800/80 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <Monitor className={`w-5 h-5 ${activeTab === 'pos-engine' ? 'text-cyan-400' : 'text-zinc-500'}`} />
                  <span className="text-base font-bold uppercase tracking-wide">2. Central Cashier POS</span>
                </div>
                <span className="text-xs font-mono text-zinc-500 font-bold">[ VIEW SYSTEM ]</span>
              </button>

              {/* Attendance Toggle */}
              <button 
                onClick={() => setActiveTab('attendance')}
                className={`w-full text-left p-5 rounded-2xl transition-all duration-300 relative overflow-hidden flex items-center justify-between border ${
                  activeTab === 'attendance' 
                  ? 'bg-zinc-900 border-blue-500 text-white shadow-xl shadow-blue-950/20' 
                  : 'bg-transparent border-zinc-800/80 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <Users className={`w-5 h-5 ${activeTab === 'attendance' ? 'text-indigo-400' : 'text-zinc-500'}`} />
                  <span className="text-base font-bold uppercase tracking-wide">3. Live Staff Attendance</span>
                </div>
                <span className="text-xs font-mono text-zinc-500 font-bold">[ VIEW SYSTEM ]</span>
              </button>

            </div>

            {/* Bottom Real-Time Benefit Context Layer */}
            <div className="bg-zinc-900/40 border border-zinc-800/80 rounded-2xl p-6 space-y-4">
              <p className="text-zinc-300 text-sm font-medium leading-relaxed font-sans">
                {structuralContent[activeTab].description}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                {structuralContent[activeTab].benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2.5 text-xs text-zinc-400 font-medium">
                    <CheckCircle className="w-4 h-4 text-blue-500 shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};

export default FeatureShowcase;